import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import isPlainObject from 'lodash/isPlainObject';
import type { ReactNode } from 'react';
import type { AppTranslations } from 'translations/translations';
import type { NotificationType } from 'utils/hooks/useNotifications';

// biome-ignore lint/correctness/noUndeclaredVariables: bypass biome issue recognizing global type declarations
type IIErrorResponse = IErrorResponse;

const getMessage = (
  error: unknown,
  messages: AppTranslations['request'],
  title: ReactNode,
) => {
  const code = (error as any)?.code;
  // If we have title we don't want show unknown message
  const defaultMsg = title === false || title === '' ? messages.unknown : '';
  const field = (error as any)?.field;
  const errMsg = field
    ? `Field ${field}: ${(error as any)?.message || defaultMsg}`
    : (error as any)?.message || defaultMsg;
  if (!code) {
    return errMsg;
  }
  const codeErrMsg = messages[code as keyof typeof messages];
  return codeErrMsg || errMsg;
};

const formatValidationErrorField = (
  field: IIErrorResponse['field'],
): string => {
  if (Array.isArray(field)) {
    return field.length > 0 ? `${field.join(' > ')}: ` : '';
  }
  if (!field) {
    return '';
  }
  return `${field}: `;
};

const formatValidationError = (error: IIErrorResponse): string =>
  `${formatValidationErrorField(error.field)}${error.message}`;
const formatValidationErrors = (errors: IIErrorResponse[]): ReactNode =>
  errors.map(formatValidationError).map((e) => <p key={e}>{e}</p>);

export const formatFlash = (
  thrownEr: unknown,
  messages: AppTranslations['request'],
  type: NotificationType = 'danger',
): {
  flashType?: NotificationType;
  message: ReactNode;
  title?: string;
} => {
  if (
    isIpfError(thrownEr, 'API') &&
    thrownEr.payload?.response.code === 'API_VALIDATION_FAILED'
  ) {
    if (thrownEr.payload.response.errors) {
      return {
        flashType: 'danger',
        message: formatValidationErrors(thrownEr.payload.response.errors),
        title: thrownEr.payload.response.message,
      };
    }
    const data = thrownEr.payload.response.data as
      | { message?: string }
      | undefined;
    if (data?.message) {
      return {
        flashType: 'danger',
        message: data.message,
        title: thrownEr.payload.response.message,
      };
    }
  }

  if (isPlainObject(thrownEr)) {
    const title =
      messages[(thrownEr as any).code as keyof typeof messages] ||
      (thrownEr as any).message ||
      false;
    const flashType = (thrownEr as any).type ?? type;
    const error = (thrownEr as any).errors?.[0];
    const message = getMessage(error, messages, title);
    return {
      flashType,
      message,
      title,
    };
  }

  if ((thrownEr as any).message === '') {
    return {
      message: messages.unknown,
    };
  }

  return {
    message: (thrownEr as any).message || `${thrownEr}`,
  };
};
